import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../../common/api/http";
import { PageSizes } from "../../../common/constants";
import { EntityIdObject, EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../common/types";
import { BailAccountSettings, CreateUpdateBailAccountSettings } from "../bailaccount/types";
import { CommissionsSettingsLevelType } from "./enums";
import {
  CommissionsSettingsLevel,
  CommissionsSettingsLevelAttachment,
  CommissionsSettingsLevelFilterPageRequest,
  CommissionsSettingsLevelFilterPageResult,
  CommissionsSettingsLevelRule,
  CreateUpdateCommissionsSettingsLevel,
  CreateUpdateCommissionsSettingsLevelRule,
} from "./types";

const FILTER_COMMISSIONS_LEVELS = ApiRequestAdapter.create("commissions-settings-levels", HttpMethod.GET);
const GET_COMMISSIONS_LEVEL = ApiRequestAdapter.create("commissions-settings-levels/{0}", HttpMethod.GET);
const CREATE_COMMISSIONS_LEVEL = ApiRequestAdapter.create("commissions-settings-levels", HttpMethod.POST);
const UPDATE_COMMISSIONS_LEVEL = ApiRequestAdapter.create("commissions-settings-levels/{0}", HttpMethod.PUT);
const DELETE_COMMISSIONS_LEVEL = ApiRequestAdapter.create("commissions-settings-levels/{0}", HttpMethod.DELETE);

const CREATE_COMMISSIONS_LEVEL_RULE = ApiRequestAdapter.create(
  "commissions-settings-levels/{0}/rules",
  HttpMethod.POST,
);
const UPDATE_COMMISSIONS_LEVEL_RULE = ApiRequestAdapter.create(
  "commissions-settings-levels/{0}/rules/{1}",
  HttpMethod.PUT,
);
const DELETE_COMMISSIONS_LEVEL_RULE = ApiRequestAdapter.create(
  "commissions-settings-levels/{0}/rules/{1}",
  HttpMethod.DELETE,
);

const CREATE_COMMISSIONS_LEVEL_BAIL_ACCOUNT_SETTINGS = ApiRequestAdapter.create(
  "commissions-settings-levels/{0}/bail-account-settings",
  HttpMethod.POST,
);
const UPDATE_COMMISSIONS_LEVEL_BAIL_ACCOUNT_SETTINGS = ApiRequestAdapter.create(
  "commissions-settings-levels/{0}/bail-account-settings",
  HttpMethod.PUT,
);
const DELETE_COMMISSIONS_LEVEL_BAIL_ACCOUNT_SETTINGS = ApiRequestAdapter.create(
  "commissions-settings-levels/{0}/bail-account-settings",
  HttpMethod.DELETE,
);

const UPLOAD_COMMISSIONS_LEVEL_ATTACHMENTS = ApiRequestAdapter.create(
  "/commissions-settings-levels/{0}/attachments",
  HttpMethod.POST,
);
const DOWNLOAD_COMMISSIONS_LEVEL_ATTACHMENT = ApiRequestAdapter.create(
  "/commissions-settings-levels/{0}/attachments/{1}",
  HttpMethod.GET,
);
const DELETE_COMMISSIONS_LEVEL_ATTACHMENT = ApiRequestAdapter.create(
  "/commissions-settings-levels/{0}/attachments/{1}",
  HttpMethod.DELETE,
);

export const commissionsSettingsLevelApi = {
  filterCommissionsLevels: (
    request: CommissionsSettingsLevelFilterPageRequest,
  ): AxiosPromise<CommissionsSettingsLevelFilterPageResult> => {
    return apiService.get(FILTER_COMMISSIONS_LEVELS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getCommissionsLevel: (request: EntityIdObject): AxiosPromise<CommissionsSettingsLevel> => {
    return apiService.get(GET_COMMISSIONS_LEVEL.getParametrizedUrl(request.id));
  },
  createCommissionsLevel: (request: CreateUpdateCommissionsSettingsLevel): AxiosPromise<CommissionsSettingsLevel> => {
    return apiService.post(CREATE_COMMISSIONS_LEVEL.url, request);
  },
  updateCommissionsLevel: (
    request: EntityObject<CreateUpdateCommissionsSettingsLevel>,
  ): AxiosPromise<CommissionsSettingsLevel> => {
    return apiService.put(UPDATE_COMMISSIONS_LEVEL.getParametrizedUrl(request.id), request.object);
  },
  deleteCommissionsLevel: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_LEVEL.getParametrizedUrl(request.id));
  },
  createCommissionsLevelRule: (
    request: EntityObject<CreateUpdateCommissionsSettingsLevelRule>,
  ): AxiosPromise<CommissionsSettingsLevelRule> => {
    return apiService.post(CREATE_COMMISSIONS_LEVEL_RULE.getParametrizedUrl(request.id), request.object);
  },
  updateCommissionsLevelRule: (
    request: TwoLevelEntityObject<CreateUpdateCommissionsSettingsLevelRule>,
  ): AxiosPromise<CommissionsSettingsLevelRule> => {
    return apiService.put(UPDATE_COMMISSIONS_LEVEL_RULE.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteCommissionsLevelRule: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_LEVEL_RULE.getParametrizedUrl(request.id1, request.id2));
  },
  createCommissionsLevelBailAccountSettings: (
    request: EntityObject<CreateUpdateBailAccountSettings>,
  ): AxiosPromise<BailAccountSettings> => {
    return apiService.post(
      CREATE_COMMISSIONS_LEVEL_BAIL_ACCOUNT_SETTINGS.getParametrizedUrl(request.id),
      request.object,
    );
  },
  updateCommissionsLevelBailAccountSettings: (
    request: EntityObject<CreateUpdateBailAccountSettings>,
  ): AxiosPromise<BailAccountSettings> => {
    return apiService.put(
      UPDATE_COMMISSIONS_LEVEL_BAIL_ACCOUNT_SETTINGS.getParametrizedUrl(request.id),
      request.object,
    );
  },
  deleteCommissionsLevelBailAccountSettings: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_LEVEL_BAIL_ACCOUNT_SETTINGS.getParametrizedUrl(request.id));
  },
  uploadCommissionsLevelAttachments: (
    request: EntityObject<FormData>,
  ): AxiosPromise<CommissionsSettingsLevelAttachment[]> => {
    return apiService.post(UPLOAD_COMMISSIONS_LEVEL_ATTACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadCommissionsLevelAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_LEVEL_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  deleteCommissionsLevelAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_LEVEL_ATTACHMENT.getParametrizedUrl(request.id1, request.id2));
  },
};

export const DEFAULT_COMMISSIONS_LEVEL_QUERY_PARAMS: Required<CommissionsSettingsLevelFilterPageRequest> = {
  pageIndex: 0,
  pageSize: PageSizes.LARGE,
  keyword: "",
  type: "" as CommissionsSettingsLevelType,
};
